// import uuid from 'uuid'
import { Casing, TEXT_ROLE_CASING } from '@common/helpers/casing'
import { PhotoSubjectType } from '@common/interfaces/photo'

export const prefix = 'ui'

/** The record a page belongs under, shown as a link before the title. See changePageParent. */
export interface IPageParent {
  title: string
  /** An in-app path, e.g. `/clients/:id` — rendered with the router, not a full URL. */
  link: string
}

/** The record the header's editable logo belongs to. Serializable — see changePageLogoSubject. */
export interface IPageLogoSubject {
  subjectType: PhotoSubjectType
  id: string
  name: string | null
  /**
   * The uploaded photo, when the page's detail payload already carries it — the CRM person,
   * company and organization pages do. Supplying it means the header paints on the first render
   * with no `GET /photos/:subjectType/:id`, which is what those payloads carry it for. Omit it
   * (the prospect/diligence/client headers) and the avatar resolves through the photo cache.
   */
  photo?: string | null
  /** Non-person fallback (a Logo.dev mark) shown when nothing has been uploaded. */
  iconUrl?: string | null
  /** Dialog title, e.g. "Company Logo". Defaults to ProfilePhotoEditor's own. */
  editorTitle?: string
  /** A profile URL (LinkedIn) offered inside the editor for the copy-and-paste route in. */
  profileUrl?: string | null
  /**
   * Show the photo but don't offer the editor. For a viewer who may read the record but not
   * change it — the logo still belongs beside the title, only the pencil doesn't.
   */
  isReadOnly?: boolean
}

export const PUT_NOTIFICATION = `${prefix}/PUT_NOTIFICATION`
export const REMOVE_NOTIFICATION = `${prefix}/REMOVE_NOTIFICATION`

export const putNotification = (notification: object) => ({
  type: PUT_NOTIFICATION,
  payload: {
    ...notification,
    isOpen: true,
    // _id: uuid(),
  },
})

export const removeNotification = () => ({
  type: REMOVE_NOTIFICATION,
  payload: {},
})

/**
 * Incoming-message toasts. Deliberately separate from PUT_NOTIFICATION: that one is
 * the single-slot snackbar for operation results (saved / failed), which replaces
 * itself and is styled as feedback on something the user just did. A message
 * arriving is unsolicited, can arrive several at once, and needs to be identifiable
 * — who sent it — so it stacks and carries its own shape.
 */
export const PUSH_MESSAGE_TOAST = `${prefix}/PUSH_MESSAGE_TOAST`
export const DISMISS_MESSAGE_TOAST = `${prefix}/DISMISS_MESSAGE_TOAST`

export interface IMessageToast {
  messageId: string
  channelId: string
  senderUserId: string
  /** Full name — the avatar needs both words to derive two initials. */
  senderName: string
  /** What the toast label renders: nickname whole, else first name. */
  senderShortName?: string
  /** Conversation label — `#channel` or the DM's title. */
  channelLabel: string
  preview: string
}

export const pushMessageToast = (toast: IMessageToast) => ({
  type: PUSH_MESSAGE_TOAST,
  payload: toast,
})

export const dismissMessageToast = (messageId: string) => ({
  type: DISMISS_MESSAGE_TOAST,
  payload: { messageId },
})

export const CHANGE_PAGE_TITLE = `${prefix}/CHANGE_PAGE_TITLE`

/** `casing: 'none'` when the title is a record's name, so it shows exactly as stored. */
export const changePageTitle = (title: string, casing: Casing = TEXT_ROLE_CASING.pageTitle) => ({
  type: CHANGE_PAGE_TITLE,
  payload: {
    title,
    casing,
  },
})

export const CHANGE_PAGE_SUBTITLE = `${prefix}/CHANGE_PAGE_SUBTITLE`

/**
 * A line of context under the page title in the global header — a person's role and where they
 * work, say. Empty clears it.
 *
 * Plain text on purpose. It sits in the one header band shared by every page, so it has to stay
 * a string the header can render and truncate without knowing which page put it there.
 */
export const changePageSubtitle = (subtitle: string) => ({
  type: CHANGE_PAGE_SUBTITLE,
  payload: {
    subtitle,
  },
})

export const CHANGE_PAGE_PARENT = `${prefix}/CHANGE_PAGE_PARENT`

/**
 * The one record this page sits under — a client, for its borrowing base and reporting pages.
 * The header renders it as a link before the page title.
 *
 * One level on purpose. It replaces the client crumb of the old per-page breadcrumb trail, which
 * was the crumb people actually used; the rest of the path is what the back-trail already covers.
 * Null clears it.
 */
export const changePageParent = (parent: IPageParent | null) => ({
  type: CHANGE_PAGE_PARENT,
  payload: {
    parent,
  },
})

export const CHANGE_PAGE_LOGO = `${prefix}/CHANGE_PAGE_LOGO`

// Optional logo shown next to the page title in the global header (e.g. a
// company/prospect logo). Pages set it via useSetPageLogo; empty = no logo.
export const changePageLogo = (logo: string) => ({
  type: CHANGE_PAGE_LOGO,
  payload: {
    logo,
  },
})

export const CHANGE_PAGE_LOGO_SUBJECT = `${prefix}/CHANGE_PAGE_LOGO_SUBJECT`

/**
 * The subject whose photo the header logo shows, when that logo is editable.
 *
 * Distinct from `changePageLogo`, which carries an already-resolved URL for a header that only
 * displays. Everything here is serializable on purpose: the upload and remove handlers are NOT
 * passed through — the header dispatches the generic `updatePhoto`/`deletePhoto` actions from the
 * subject type and id, which is what makes one editable header work for every subject instead of
 * each page wiring its own. Null clears it.
 */
export const changePageLogoSubject = (subject: IPageLogoSubject | null) => ({
  type: CHANGE_PAGE_LOGO_SUBJECT,
  payload: {
    subject,
  },
})
